// StandardLoader — Toggle normas on/off com persistência localStorage
import { useState, useEffect, useCallback } from "react";
import { Settings, Lock, Check, Clock, Upload } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import NormBadge from "../../components/NormBadge";
import { STANDARDS_REGISTRY, getActiveStandards, getAvailableStandards, getPlannedStandards, getLockedStandards } from "./registry";
import { T } from "../../i18n";
import { useConfigStore } from "../../stores/configStore";

const STORAGE_KEY = "norm_standards_toggle";

function loadToggleState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {}
  // First load: activate standards with defaultOn: true
  const defaults = {};
  STANDARDS_REGISTRY.filter(s => s.defaultOn).forEach(s => { defaults[s.id] = true; });
  return defaults;
}

function saveToggleState(state) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (e) {}
}

export function useStandardsToggle() {
  const [toggleState, setToggleState] = useState(loadToggleState);

  const toggle = useCallback((id) => {
    setToggleState(prev => {
      const next = { ...prev, [id]: !prev[id] };
      saveToggleState(next);
      return next;
    });
  }, []);

  const isActive = useCallback((id) => {
    const std = STANDARDS_REGISTRY.find(s => s.id === id);
    if (!std) return false;
    if (std.status === "locked") return true;
    if (toggleState[id] === true) return true;
    if (toggleState[id] === false) return false;
    return !!std.defaultOn;
  }, [toggleState]);

  const activeStandards = getActiveStandards(toggleState);
  const activeCount = activeStandards.length;
  const loadedCount = getAvailableStandards().length;
  const plannedCount = getPlannedStandards().length;

  return { toggleState, toggle, isActive, activeStandards, activeCount, loadedCount, plannedCount };
}

export function StandardLoader({ open, onOpenChange }) {
  const lang = useConfigStore(s => s.lang);
  const { toggleState, toggle, isActive } = useStandardsToggle();
  const locked = getLockedStandards();
  const available = getAvailableStandards();
  const planned = getPlannedStandards();

  const activeCount = locked.length + available.filter(s => toggleState[s.id] === true || (s.defaultOn && toggleState[s.id] !== false)).length;
  const loadedCount = available.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-primary" />
            <DialogTitle className="font-display text-2xl">Gerenciar Normas</DialogTitle>
          </div>
          <DialogDescription>
            {activeCount} ativas · {loadedCount} disponíveis{planned.length > 0 ? ` · ${planned.length} planejadas` : ""}
          </DialogDescription>
          <div className="mt-2 px-3 py-2 bg-muted rounded-md border border-border">
            <p className="text-[10px] text-muted-foreground leading-relaxed">{T[lang]?.normDisclaimer || "As normas ISO são propriedade intelectual da ISO/ABNT. O usuário é responsável por possuir licença válida das normas que utiliza."}</p>
          </div>
        </DialogHeader>

        <div className="overflow-y-auto max-h-[65vh] pr-1 space-y-1">
          {/* LOCKED — apenas ISO 19011 */}
          <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-muted-foreground mb-2 mt-2">{T[lang]?.normInfraLabel || "infraestrutura — sempre ativa"}</div>
          {locked.map(s => (
            <div key={s.id} className="flex items-center gap-3 px-4 py-3 bg-secondary/30 rounded-md border border-border">
              <NormBadge std={s} size="md" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-foreground">{s.name}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">{s.desc}</div>
              </div>
              <div className="flex items-center gap-2">
                <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                <Badge variant="success" className="text-[8px]">LOCKED</Badge>
              </div>
            </div>
          ))}

          {/* AVAILABLE — toggle */}
          <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-muted-foreground mb-2 mt-4">normas disponíveis — toggle on/off</div>
          {available.map(s => {
            const active = toggleState[s.id] === true || (s.defaultOn && toggleState[s.id] !== false);
            return (
              <div
                key={s.id}
                onClick={() => toggle(s.id)}
                className={`flex items-center gap-3 px-4 py-3 rounded-md border cursor-pointer transition-colors ${
                  active
                    ? "bg-primary/8 border-primary/40 hover:bg-primary/12"
                    : "bg-card border-border hover:bg-secondary/50"
                }`}
              >
                <NormBadge std={s} size="md" />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-foreground">{s.name}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{s.desc}</div>
                  <div className="text-[9px] text-muted-foreground font-mono mt-1 tracking-wider uppercase">{s.category}</div>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-10 h-5 rounded-full flex items-center transition-colors ${active ? "bg-primary" : "bg-border"}`}>
                    <div className={`w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${active ? "translate-x-5" : "translate-x-0.5"}`} />
                  </div>
                  {active
                    ? <Badge variant="success" className="text-[8px]">ACTIVE</Badge>
                    : <Badge variant="secondary" className="text-[8px]">LOADED</Badge>
                  }
                </div>
              </div>
            );
          })}

          {/* PLANNED — só renderiza se houver normas planejadas */}
          {planned.length > 0 && (
            <>
              <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-muted-foreground mb-2 mt-4">planejadas — em desenvolvimento</div>
              {planned.map(s => (
                <div key={s.id} className="flex items-center gap-3 px-4 py-3 bg-card/50 rounded-md border border-border/50 opacity-60" title="Em desenvolvimento">
                  <div className="opacity-50">
                    <NormBadge std={s} size="md" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-muted-foreground">{s.name}</div>
                    <div className="text-[11px] text-muted-foreground/70 mt-0.5">{s.desc}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-3.5 w-3.5 text-muted-foreground/50" />
                    <Badge variant="outline" className="text-[8px] opacity-50">PLANNED</Badge>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Button that opens the StandardLoader dialog
export function StandardLoaderButton() {
  const [open, setOpen] = useState(false);
  const { activeCount, loadedCount, plannedCount } = useStandardsToggle();

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="font-mono flex items-center gap-2 px-3 py-2 border border-border rounded-md bg-card text-muted-foreground text-[10px] cursor-pointer hover:bg-secondary hover:text-foreground transition-colors tracking-wider uppercase"
      >
        <Settings className="h-3.5 w-3.5" />
        {activeCount} ativas · {loadedCount} disponíveis{plannedCount > 0 ? ` · ${plannedCount} planejadas` : ""}
      </button>
      <StandardLoader open={open} onOpenChange={setOpen} />
    </>
  );
}
